// BULK Trader Revamp
import { gql, useMutation, useQuery } from '@apollo/client';
import ConfirmationModal from '@components/modals/confirmationModal';
import BulkTradeRunAllocationsTable from '@components/tables/bulkTradeRunAllocationsTable/bulkTradeRunAllocationsTable';
import TradeRequestsTable from '@components/tables/tradeRequestsTable/tradeRequestsTable';
import { Exchanges } from '@interfaces/financialProduct';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // no icons in ovComponents
import { Step, StepLabel, Stepper } from '@mui/material'; // no steps in ovComponents
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Card } from '@ovComponents/2-component/card/card';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalToast } from '@providers/globalToastProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  type AllocationFileRecord,
  type BulkTradeFileRecord,
  getAllocationFileDownloadLabel,
  getBulkTradeFileDownloadLabel,
} from '../bulkTradeFileLabels';
import UpdateMutualFundTradesTable from './components/updateMutualFundTradesTable';
import UpdateTradesTable from './components/updateTradesTable';

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun($bulkTradeRunId: ObjectID!, $PTF: Boolean) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        createdAt
        buyBulkTradeFileUrl
        sellBulkTradeFileUrl
        mutualFundFileUrl
        state
        bulkPTFFileUrls
        bulkTradeFiles {
          type
          requestType
          url
        }
        allocationFiles {
          requestType
          url
          type
        }
        incompleteTradeRequestsByExchange(PTF: $PTF) {
          exchange
          total
        }
        allTradeRequestsByExchange {
          exchange
        }
      }
    }
  }
`;

const TRANSITION_BULK_TRADE_RUN = gql`
  mutation transitionBulkTradeRun($input: TransitionBulkTradeRunInput!) {
    transitionBulkTradeRun(input: $input) {
      bulkTradeRun {
        id
        state
      }
    }
  }
`;

export enum BulkTradeRunStates {
  GENERATING = 'GENERATING',
  BULK_TRADES_INITIATED = 'BULK_TRADES_INITIATED',
  BULK_TRADES_FILES_GENERATING = 'BULK_TRADES_FILES_GENERATING',
  BULK_TRADES_REQUESTED = 'BULK_TRADES_REQUESTED',
  BULK_TRADES_COMPLETED = 'BULK_TRADES_COMPLETED',
  ALLOCATIONS_INITIATED = 'ALLOCATIONS_INITIATED',
  ALLOCATIONS_REQUESTED = 'ALLOCATIONS_REQUESTED',
  ALLOCATIONS_COMPLETED = 'ALLOCATIONS_COMPLETED',
  COMPLETED = 'COMPLETED',
  COMPLETED_PENDING_PTFS = 'COMPLETED_PENDING_PTFS',
  CANCELED = 'CANCELED',
}

enum BulkTraderAdditionalStates {
  REVIEW_TRADE_ALLOCATIONS = 'REVIEW_TRADE_ALLOCATIONS',
  REVIEW_PTF_TRADE_ALLOCATIONS = 'REVIEW_PTF_TRADE_ALLOCATIONS',
}

type BulkTradeRun = {
  id: string;
  createdAt: string;
  bulkTradeFiles?: BulkTradeFileRecord[];
  allocationFiles?: AllocationFileRecord[];
  buyBulkTradeFileUrl?: string;
  sellBulkTradeFileUrl?: string;
  mutualFundFileUrl?: string;
  state: BulkTradeRunStates;
  bulkPTFFileUrls?: string[];
  incompleteTradeRequestsByExchange: {
    exchange: string;
    total: number;
  }[];
  allTradeRequestsByExchange: {
    exchange: string;
  }[];
};

const POLLING_TIME = 1500;

const BulkTrader = () => {
  const { t } = useTranslation('bulkTrader');
  const params = useParams();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { showToast } = useGlobalToast();
  const bulkTradeRunId = params.id;
  const [activeState, setActiveState] = useState<
    BulkTradeRunStates | BulkTraderAdditionalStates
  >();
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [discrepancyConfirmationOpen, setDiscrepancyConfirmationOpen] =
    useState(false);
  const [buyTradesWarnings, setBuyTradesWarnings] = useState(false);
  const [sellTradesWarnings, setSellTradesWarnings] = useState(false);
  const [tradesSaved, setTradesSaved] = useState<boolean>(false);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const getTradeCount = (fetchedBulkTradeRun?: BulkTradeRun): number =>
    fetchedBulkTradeRun?.incompleteTradeRequestsByExchange?.reduce(
      (total, item) => total + item.total,
      0,
    ) ?? 0;
  const getAffectedAccountCount = (
    fetchedBulkTradeRun?: BulkTradeRun,
  ): number => fetchedBulkTradeRun?.allTradeRequestsByExchange?.length ?? 0;

  const getSteps = ({
    hasBulkTradeOrMutualFundExchanges = false,
    hasPTFExchanges = false,
  }: {
    hasBulkTradeOrMutualFundExchanges?: boolean;
    hasPTFExchanges?: boolean;
  }): {
    states: (BulkTradeRunStates | BulkTraderAdditionalStates)[];
    label: string;
  }[] => [
    { states: [BulkTradeRunStates.GENERATING], label: t('generating') },
    {
      states: [BulkTradeRunStates.BULK_TRADES_INITIATED],
      label: t('reviewTrades'),
    },
    ...(hasBulkTradeOrMutualFundExchanges
      ? [
          {
            states: [
              BulkTradeRunStates.BULK_TRADES_REQUESTED,
              BulkTradeRunStates.BULK_TRADES_COMPLETED,
            ],
            label: t('updateTrades'),
          },
          {
            states: [
              BulkTradeRunStates.ALLOCATIONS_REQUESTED,
              BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS,
            ],
            label: t('reviewTradeAllocations'),
          },
        ]
      : []),
    ...(hasPTFExchanges
      ? [
          {
            states: [
              BulkTradeRunStates.BULK_TRADES_REQUESTED,
              BulkTradeRunStates.COMPLETED_PENDING_PTFS,
            ],
            label: t('updatePTFTrades'),
          },
          {
            states: [
              BulkTradeRunStates.ALLOCATIONS_REQUESTED,
              BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
            ],
            label: t('reviewPTFTradeAllocations'),
          },
        ]
      : []),
    { states: [BulkTradeRunStates.COMPLETED], label: t('completed') },
  ];

  const [steps, setSteps] = useState<ReturnType<typeof getSteps>>(getSteps({}));

  const stateToStep = (
    state: BulkTradeRunStates | BulkTraderAdditionalStates,
  ) => steps.findIndex((step) => step.states.includes(state));

  const { loading, startPolling, stopPolling, refetch, data } = useQuery<{
    fetchBulkTradeRun: { bulkTradeRun?: BulkTradeRun };
  }>(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
      PTF:
        activeState &&
        [
          BulkTradeRunStates.COMPLETED_PENDING_PTFS,
          BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
        ].includes(activeState),
    },
    fetchPolicy: 'no-cache',
    pollInterval: POLLING_TIME,
    notifyOnNetworkStatusChange: true,
    onCompleted: (e) => {
      const { bulkTradeRun: fetchedBulkTradeRun } = e.fetchBulkTradeRun;
      const { state } = fetchedBulkTradeRun ?? {};

      if (state !== BulkTradeRunStates.GENERATING) {
        const hasBulkTradeOrMutualFundExchanges =
          fetchedBulkTradeRun?.allTradeRequestsByExchange?.some(
            (item: { exchange: string }) => item.exchange !== Exchanges.PTF,
          ) ?? false;
        const hasPTFExchanges =
          fetchedBulkTradeRun?.allTradeRequestsByExchange?.some(
            (item: { exchange: string }) => item.exchange === Exchanges.PTF,
          ) ?? false;

        setSteps(
          getSteps({ hasBulkTradeOrMutualFundExchanges, hasPTFExchanges }),
        );
      }

      setActiveState(state);

      if (
        state &&
        (
          [
            BulkTradeRunStates.BULK_TRADES_REQUESTED,
            BulkTradeRunStates.ALLOCATIONS_REQUESTED,
          ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
        ).includes(state)
      ) {
        startPolling(POLLING_TIME);
        setLocalLoading(true);
      } else if (state === BulkTradeRunStates.COMPLETED) {
        sendAnalytic(analyticsEvents.bulkTraderDetailComplete, {
          bulkTradeRunId: fetchedBulkTradeRun?.id,
          timestamp: new Date().toISOString(),
        });
        stopPolling();
        setLocalLoading(false);
      } else if (state === BulkTradeRunStates.CANCELED) {
        sendAnalytic(analyticsEvents.bulkTraderDetailCancel, {
          bulkTradeRunId: fetchedBulkTradeRun?.id,
          currentState: state,
          affectedAccountCount: getAffectedAccountCount(fetchedBulkTradeRun),
          tradeCount: getTradeCount(fetchedBulkTradeRun),
          failureStep: activeState ?? state,
          timestamp: new Date().toISOString(),
        });
        stopPolling();
        setLocalLoading(false);
      } else if (state && stateToStep(state) > 0) {
        stopPolling();
        setLocalLoading(false);
      }
    },
  });

  const bulkTradeRun = data?.fetchBulkTradeRun?.bulkTradeRun;

  const [transitionBulkTradeRun, { loading: transitionLoading }] = useMutation(
    TRANSITION_BULK_TRADE_RUN,
    {
      onError: (error) => {
        sendAnalytic(analyticsEvents.bulkTraderRunError, {
          bulkTradeRunId: bulkTradeRun?.id,
          errorType: error.message,
          affectedAccountCount: getAffectedAccountCount(bulkTradeRun),
          tradeCount: getTradeCount(bulkTradeRun),
          failureStep: activeState ?? 'unknown',
          timestamp: new Date().toISOString(),
        });
      },
    },
  );

  useEffect(() => {
    if (
      bulkTradeRun &&
      activeState &&
      (
        [
          BulkTradeRunStates.BULK_TRADES_COMPLETED,
          BulkTradeRunStates.COMPLETED_PENDING_PTFS,
        ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
      ).includes(activeState)
    ) {
      setTradesSaved(
        bulkTradeRun.incompleteTradeRequestsByExchange.length === 0,
      );
    }
  }, [activeState, bulkTradeRun]);

  const onClickNext = async () => {
    if (buyTradesWarnings || sellTradesWarnings) {
      setDiscrepancyConfirmationOpen(true);
    } else {
      await goNext();
    }
  };

  const reloadBulkTradeWithPolling = () => {
    startPolling(POLLING_TIME);
    setLocalLoading(true);
  };

  const goNext = async () => {
    if (!bulkTradeRun) return;

    // Track save analytics when moving to next step after saving trades
    if (
      activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED ||
      activeState === BulkTradeRunStates.COMPLETED_PENDING_PTFS
    ) {
      sendAnalytic(analyticsEvents.bulkTraderDetailSave, {
        bulkTradeRunId: bulkTradeRun.id,
        currentState: activeState,
        timestamp: new Date().toISOString(),
      });
    }

    if (activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED) {
      setActiveState(BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS);
      return;
    }

    if (activeState === BulkTradeRunStates.COMPLETED_PENDING_PTFS) {
      setActiveState(BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS);
      return;
    }

    await transitionBulkTradeRun({
      variables: {
        input: {
          bulkTradeRunId: bulkTradeRun.id,
          transition: 'initiateAllocations',
        },
      },
    });

    reloadBulkTradeWithPolling();
  };

  const goBack = async () => {
    if (!bulkTradeRun) return;

    if (activeState === BulkTradeRunStates.BULK_TRADES_COMPLETED) {
      await transitionBulkTradeRun({
        variables: {
          input: {
            bulkTradeRunId: bulkTradeRun.id,
            transition: 'resetBulkTrades',
          },
        },
      });
      setActiveState(BulkTradeRunStates.BULK_TRADES_INITIATED);
      return;
    }

    reloadBulkTradeWithPolling();
  };

  const emitTransitionBulkTradeRun = async (
    transition: 'resetBulkTrades' | 'cancel' | 'generateBulkTradeFiles',
  ) => {
    if (!bulkTradeRun) return;

    // Track transition analytics
    sendAnalytic(analyticsEvents.bulkTraderDetailTransition, {
      bulkTradeRunId: bulkTradeRun.id,
      transition,
      currentState: activeState,
      timestamp: new Date().toISOString(),
    });

    if (transition === 'cancel') {
      sendAnalytic(analyticsEvents.bulkTraderRunAbandon, {
        bulkTradeRunId: bulkTradeRun.id,
        currentState: activeState,
        timestamp: new Date().toISOString(),
      });
    }

    await transitionBulkTradeRun({
      variables: { input: { bulkTradeRunId: bulkTradeRun.id, transition } },
    });

    reloadBulkTradeWithPolling();
  };

  return (
    <Box data-testid='bulk-trader-container'>
      {loading || !bulkTradeRun ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='bulk-trader-loading'
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : bulkTradeRun.state === BulkTradeRunStates.CANCELED ? (
        <Typography
          variant='headingLarge'
          sx={{ textAlign: 'center', marginTop: 5, color: 'error.main' }}
          testId='bulk-trader-canceled'
        >
          {t('bulkTradeRunCanceled')}
        </Typography>
      ) : (
        <>
          <Box
            data-testid='bulk-trader-main-content'
            sx={{ width: '100%', mt: 5 }}
          >
            <Card variant='elevated' sx={{ width: '100%' }}>
              <Stepper
                activeStep={activeState ? stateToStep(activeState) : undefined}
                sx={{ p: 5 }}
                data-testid='bulk-trader-stepper'
              >
                {steps.map(({ label }, index) => {
                  const stepProps: { completed?: boolean } = {};
                  return (
                    <Step
                      key={label}
                      {...stepProps}
                      data-testid={`bulk-trader-step-${index}`}
                    >
                      <StepLabel
                        data-testid={`bulk-trader-step-label-${index}`}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              {!activeState ||
              activeState === BulkTradeRunStates.GENERATING ||
              localLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: '350px',
                  }}
                  data-testid='bulk-trader-processing'
                >
                  <Box textAlign='center'>
                    <CircularProgress size={60} />
                  </Box>
                </Box>
              ) : (
                <>
                  {activeState === BulkTradeRunStates.BULK_TRADES_INITIATED && (
                    <>
                      <Box data-testid='bulk-trader-review-trades-section'>
                        <TradeRequestsTable
                          filter={{ bulkTradeRunId: bulkTradeRun.id }}
                          showClient={true}
                          showExpected={true}
                        />
                        <Box
                          display='flex'
                          justifyContent='space-between'
                          flexWrap='wrap'
                        >
                          <Button
                            variant='outlined'
                            color='destructive'
                            sx={{ m: 1, textAlign: 'right' }}
                            disabled={transitionLoading}
                            onClick={() => setCancelConfirmationOpen(true)}
                            label={t('cancel')}
                            dataTestId='bulk-trader-cancel-button'
                          />
                          <Button
                            variant='filled'
                            sx={{ m: 1, textAlign: 'right' }}
                            disabled={transitionLoading}
                            onClick={() =>
                              emitTransitionBulkTradeRun(
                                'generateBulkTradeFiles',
                              )
                            }
                            label={t('next')}
                            dataTestId='bulk-trader-next-button'
                          />
                        </Box>
                      </Box>
                    </>
                  )}

                  {activeState &&
                    (
                      [
                        BulkTradeRunStates.BULK_TRADES_COMPLETED,
                        BulkTradeRunStates.COMPLETED_PENDING_PTFS,
                      ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
                    ).includes(activeState) && (
                      <>
                        <Box data-testid='bulk-trader-update-trades-section'>
                          <Box display='flex' flexWrap='wrap' sx={{ p: 1 }}>
                            {bulkTradeRun.buyBulkTradeFileUrl && (
                              <Button
                                variant='filled'
                                sx={{ marginRight: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadBuyFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'buy',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.buyBulkTradeFileUrl}
                                download
                                label={t('buyTradeFile')}
                                dataTestId='bulk-trader-download-buy-file'
                              />
                            )}
                            {bulkTradeRun.sellBulkTradeFileUrl && (
                              <Button
                                variant='filled'
                                sx={{ marginRight: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadSellFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'sell',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.sellBulkTradeFileUrl}
                                download
                                label={t('sellTradeFile')}
                                dataTestId='bulk-trader-download-sell-file'
                              />
                            )}
                            {bulkTradeRun.mutualFundFileUrl && (
                              <Button
                                variant='filled'
                                sx={{ marginRight: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Track mutual fund file download
                                  sendAnalytic(
                                    analyticsEvents.bulkTraderDetailDownloadMutualFundFile,
                                    {
                                      bulkTradeRunId: bulkTradeRun.id,
                                      fileType: 'mutualFund',
                                      timestamp: new Date().toISOString(),
                                    },
                                  );
                                }}
                                href={bulkTradeRun.mutualFundFileUrl}
                                download
                                label={t('mutualFundTradeFile')}
                                dataTestId='bulk-trader-download-mutual-fund-file'
                              />
                            )}
                            {bulkTradeRun.bulkPTFFileUrls?.map(
                              (fileUrl: string, index: number) => (
                                <Button
                                  key={fileUrl}
                                  variant='filled'
                                  sx={{ marginRight: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sendAnalytic(
                                      analyticsEvents.bulkTraderDetailDownloadPtfFile,
                                      {
                                        bulkTradeRunId: bulkTradeRun.id,
                                        fileType: 'ptf',
                                        fileIndex: index + 1,
                                        timestamp: new Date().toISOString(),
                                      },
                                    );
                                  }}
                                  href={fileUrl}
                                  download
                                  label={`${t('ptfBulkTradeFile')}: ${index + 1}`}
                                  dataTestId={`bulk-trader-download-ptf-file-${index + 1}`}
                                />
                              ),
                            )}
                            {bulkTradeRun?.bulkTradeFiles?.map(
                              (
                                file: BulkTradeFileRecord,
                                fileIndex: number,
                              ) => {
                                const sameTypeCount =
                                  bulkTradeRun?.bulkTradeFiles
                                    ?.slice(0, fileIndex + 1)
                                    ?.filter(
                                      (f: BulkTradeFileRecord) =>
                                        f.type === file.type,
                                    ).length ?? 0;

                                const totalSameType =
                                  bulkTradeRun?.bulkTradeFiles?.filter(
                                    (f: BulkTradeFileRecord) =>
                                      f.type === file.type,
                                  ).length ?? 0;

                                const fileLabel = getBulkTradeFileDownloadLabel(
                                  file,
                                  t,
                                  sameTypeCount,
                                  totalSameType,
                                );

                                return (
                                  <Button
                                    key={file.url}
                                    variant='filled'
                                    sx={{ marginRight: 1 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailDownloadBulkTradeFile,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          fileType: file.type,
                                          fileIndex: fileIndex + 1,
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                    }}
                                    href={file.url}
                                    download
                                    label={fileLabel}
                                    dataTestId={`bulk-trader-download-bulk-trade-file-${fileIndex}`}
                                  />
                                );
                              },
                            )}
                            {bulkTradeRun?.allocationFiles?.map(
                              (
                                file: AllocationFileRecord,
                                fileIndex: number,
                              ) => {
                                const sameTypeCount =
                                  bulkTradeRun?.allocationFiles
                                    ?.slice(0, fileIndex + 1)
                                    ?.filter(
                                      (f: AllocationFileRecord) =>
                                        f.type === file.type,
                                    ).length ?? 0;

                                const totalSameType =
                                  bulkTradeRun?.allocationFiles?.filter(
                                    (f: AllocationFileRecord) =>
                                      f.type === file.type,
                                  ).length ?? 0;

                                const fileLabel =
                                  getAllocationFileDownloadLabel(
                                    file,
                                    t,
                                    sameTypeCount,
                                    totalSameType,
                                  );

                                return (
                                  <Button
                                    key={file.url}
                                    variant='filled'
                                    sx={{ marginRight: 1 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Track allocation file download
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailDownloadAllocationFileNew,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          fileType: file.type,
                                          fileIndex: fileIndex + 1,
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                    }}
                                    href={file.url}
                                    download
                                    label={fileLabel}
                                    dataTestId={`bulk-trader-download-allocation-file-${fileIndex}`}
                                  />
                                );
                              },
                            )}
                          </Box>
                          <Box
                            display='flex'
                            flexWrap='wrap'
                            gap={2}
                            sx={{ p: 2 }}
                          >
                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-buy-trades-table'>
                                  <UpdateTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='BUY'
                                    setDiscrepancyWarning={setBuyTradesWarnings}
                                    exchanges={
                                      activeState ===
                                      BulkTradeRunStates.COMPLETED_PENDING_PTFS
                                        ? [Exchanges.PTF]
                                        : []
                                    }
                                    onPriceUpdate={() => {
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailUpdate,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          updateType: 'BUY',
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                      refetch();
                                    }}
                                  />
                                </Box>
                              </Card>
                            </Box>

                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-sell-trades-table'>
                                  <UpdateTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='SELL'
                                    setDiscrepancyWarning={
                                      setSellTradesWarnings
                                    }
                                    exchanges={
                                      activeState ===
                                      BulkTradeRunStates.COMPLETED_PENDING_PTFS
                                        ? [Exchanges.PTF]
                                        : []
                                    }
                                    onPriceUpdate={() => {
                                      sendAnalytic(
                                        analyticsEvents.bulkTraderDetailUpdate,
                                        {
                                          bulkTradeRunId: bulkTradeRun.id,
                                          updateType: 'SELL',
                                          timestamp: new Date().toISOString(),
                                        },
                                      );
                                      refetch();
                                    }}
                                  />
                                </Box>
                              </Card>
                            </Box>
                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-buy-mutual-fund-trades-table'>
                                  <UpdateMutualFundTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='BUY'
                                  />
                                </Box>
                              </Card>
                            </Box>

                            <Box sx={{ flex: '1 1 40em', minWidth: '40em' }}>
                              <Card variant='elevated' sx={{ width: '100%' }}>
                                <Box data-testid='bulk-trader-sell-mutual-fund-trades-table'>
                                  <UpdateMutualFundTradesTable
                                    bulkTradeRunId={bulkTradeRun.id}
                                    type='SELL'
                                  />
                                </Box>
                              </Card>
                            </Box>
                          </Box>
                          <Box
                            display='flex'
                            justifyContent='space-between'
                            flexWrap='wrap'
                            alignItems='flex-start'
                          >
                            <Box>
                              {activeState ===
                                BulkTradeRunStates.BULK_TRADES_COMPLETED && (
                                <>
                                  <Button
                                    variant='outlined'
                                    color='destructive'
                                    sx={{ m: 1, textAlign: 'right' }}
                                    disabled={transitionLoading}
                                    onClick={() =>
                                      setCancelConfirmationOpen(true)
                                    }
                                    label={t('cancel')}
                                    dataTestId='bulk-trader-cancel-trades-button'
                                  />
                                  <Button
                                    variant='outlined'
                                    color='destructive'
                                    sx={{ m: 1, textAlign: 'right' }}
                                    disabled={transitionLoading}
                                    onClick={goBack}
                                    label={t('back')}
                                    dataTestId='bulk-trader-back-button'
                                  />
                                </>
                              )}
                            </Box>
                            <Box>
                              {!tradesSaved ? (
                                <Tooltip title={t('saveTradesMessage')}>
                                  <span>
                                    <Button
                                      variant='filled'
                                      sx={{ m: 1, textAlign: 'right' }}
                                      disabled={!tradesSaved}
                                      onClick={onClickNext}
                                      label={t('next')}
                                      dataTestId='bulk-trader-save-and-next-button'
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <Button
                                  variant='filled'
                                  sx={{ m: 1, textAlign: 'right' }}
                                  onClick={onClickNext}
                                  label={t('next')}
                                  dataTestId='bulk-trader-save-and-next-button'
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}

                  {activeState &&
                    (
                      [
                        BulkTraderAdditionalStates.REVIEW_TRADE_ALLOCATIONS,
                        BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
                      ] as (BulkTradeRunStates | BulkTraderAdditionalStates)[]
                    ).includes(activeState) && (
                      <>
                        <Box data-testid='bulk-trader-review-allocations-section'>
                          <BulkTradeRunAllocationsTable
                            filter={{
                              bulkTradeRunId: bulkTradeRun.id,
                              isPTF:
                                activeState ===
                                BulkTraderAdditionalStates.REVIEW_PTF_TRADE_ALLOCATIONS,
                            }}
                          />
                          <Box display='flex' justifyContent='flex-end'>
                            <Button
                              variant='filled'
                              sx={{ m: 1, textAlign: 'right' }}
                              onClick={() => goNext()}
                              label={t('next')}
                              dataTestId='bulk-trader-complete-allocations-button'
                            />
                          </Box>
                        </Box>
                      </>
                    )}

                  {activeState === BulkTradeRunStates.COMPLETED && (
                    <>
                      <Box
                        sx={{ p: 15 }}
                        data-testid='bulk-trader-completed-section'
                      >
                        <Box
                          display='flex'
                          justifyContent='center'
                          data-testid='bulk-trader-completed-icon'
                        >
                          <CheckCircleIcon
                            color='success'
                            sx={{ fontSize: '100px' }}
                          />
                        </Box>
                        <Typography
                          variant='headingLarge'
                          sx={{
                            textAlign: 'center',
                            p: 3,
                            color: 'primary.main',
                          }}
                          testId='bulk-trader-completed-message'
                        >
                          {t('bulkTradeRunCompleted')}
                        </Typography>
                      </Box>
                      <Box data-testid='bulk-trader-completed-trades-table'>
                        <TradeRequestsTable
                          filter={{ bulkTradeRunId: bulkTradeRun.id }}
                          showClient={true}
                          showExpected={true}
                          bulkTradeRunState={bulkTradeRun.state}
                        />
                      </Box>
                    </>
                  )}
                </>
              )}
            </Card>
          </Box>

          <ConfirmationModal
            open={cancelConfirmationOpen}
            onCancel={() => setCancelConfirmationOpen(false)}
            onConfirm={async () => {
              await emitTransitionBulkTradeRun('cancel');
              showToast({
                severity: 'info',
                message: t('cancelTriggeredMessage'),
              });
              setCancelConfirmationOpen(false);
            }}
            loading={loading}
            bodyText={t('cancelConfirmationDialog')}
            title={t('cancelBulkTradeRunTrades')}
            data-testid='bulk-trader-cancel-confirmation-modal'
          />

          <ConfirmationModal
            open={discrepancyConfirmationOpen}
            onCancel={() => setDiscrepancyConfirmationOpen(false)}
            onConfirm={() => {
              setDiscrepancyConfirmationOpen(false);
              goNext();
            }}
            title={t('discrepancyConfirmationDialog.title')}
            bodyText={t('discrepancyConfirmationDialog.text')}
            data-testid='bulk-trader-discrepancy-confirmation-modal'
          />
        </>
      )}
    </Box>
  );
};

export default BulkTrader;
